import styles from './ccf-text-editor.module.scss';
import { Editor } from 'react-draft-wysiwyg';
import {
  ContentState,
  EditorState,
  Modifier,
  convertToRaw,
  convertFromHTML,
  BlockMapBuilder,
  CompositeDecorator,
  AtomicBlockUtils } from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CcfUploadIcon,
  CcfEmojiIcon,
  CcfImageIcon,
  CcfGifIcon,
  CcfBox,
  CcfButton,
  CcfFlashIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';
import {
  activeContactSelector,
  getActiveInteraction,
  getCopilotConfig,
  getIsInsert,
  getIsResponseInserted,
  getIsResponseSent,
  getIsSent,
  getMessageList,
  getSentBestResponse,
  getcopilotFlowMsgIndex,
  getSelectedCopilotAssets,
  setSelectedCopilotAssets,
  setIsAssetsAddedToCopilotSuggestion,
  updateFlowMsgIndex,
  setIsInsertClicked,
  setIsSentClicked,
} from '@nice-ccf/ui-components';
import {
  DigitalChannelStatus,
  DigitalChannelType,
  VoiceContactStatus,
} from '@nice-ccf/core-sdk';
import {
  getSentQReply,
  updateSentQReply,
} from '../ccf-app-space/ccf-app-space.slice';
import {
  getActiveInteractionID,
  updateAppSpaceTab,
  getContactElevationDetails,
  updateTwitterAgentReply,
  updateActiveContact,
} from '../ccf-inbox/ccf-inbox.slice';
import { AppSpace } from '../../enums/app-space-tabs';
import { CancelOutlined, Send } from '@mui/icons-material';
import {
  getInteractionMsgCount,
  interactionSliceActions,
} from '../ccf-interaction-workspace/ccf-interaction-space.slice';
import { Button, Theme, useTheme } from '@mui/material';
import ccfEditorStyles from './ccf-text-editor-styles';

import {
  FormatTextdirectionLToR,
  FormatTextdirectionRToL,
} from '@mui/icons-material';
import { API_BASE_URL, callApi, PROCESS_UTTERANCE } from '../ccf-app-space/ccf-copilot/ccf-copilot-api';
import { v5 as uuidv5, NIL as namespaceNIL } from 'uuid';

/* eslint-disable-next-line */
export interface CcfTextEditorProps {
  getTextCB?: any;
  /**
   * true for scripted (JSON) digital chats: manual sends advance the copilot flow
   * and knowledge-base links/images are staged and sent as structured content
   */
  advanceCopilotFlow?: boolean;
}

/**
 * Decodes the 'Link :<url>' / 'IMAGE :<url>' markers emitted by the copilot helper
 * @param response - string
 * @example parseAssetMarker('Link :https://example.com')
 * @returns the decoded asset, or null for plain text
 */
const parseAssetMarker = (response: any) => {
  if (typeof response !== 'string') {
    return null;
  }
  if (response.startsWith('Link')) {
    return { type: 'link', value: response.slice(6) };
  }
  if (response.startsWith('IMAGE')) {
    const imageText = response.slice(6);
    const value = imageText.includes('http')
      ? imageText.substring(imageText.indexOf('http')).trim()
      : response.split(':')[1];
    return { type: 'image', value };
  }
  return null;
};

/**
 * Component used for text editor
 * @param props - CcfTextEditorProps
 * @example <CcfTextEditor />
 * @returns
 */
export function CcfTextEditor(props: CcfTextEditorProps) {
  const [translate] = useTranslator();
  const [currentVal, setCurrentVal] = useState('');
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [data, setData] = useState('');
  const dispatch = useDispatch();
  const flowMsgIndex = useSelector(getcopilotFlowMsgIndex);
  const selectedCopilotAssets = useSelector(getSelectedCopilotAssets);
  const stopFlowJourneyRef = useRef(false);
  const { advanceCopilotFlow } = props;
  const hasStagedAssets =
    !!advanceCopilotFlow &&
    (selectedCopilotAssets.webLinks.length > 0 ||
      selectedCopilotAssets.images.length > 0 ||
      selectedCopilotAssets.processes.length > 0);
  const interactionMSgCount = useSelector(getInteractionMsgCount);
  const selectedCallContact = useSelector(activeContactSelector);
  const activecontact = useSelector(activeContactSelector);
  const contactElevatedDetails = useSelector(getContactElevationDetails);
  const activeInteraction = useSelector(getActiveInteraction);
  const { setInteractionMsgCount } = interactionSliceActions;
  const activecontactId = activecontact?.id || '201251606723';
  const sentBestResponse = useSelector(getSentBestResponse(activecontactId));
  const isResponseInserted: boolean | undefined = useSelector(
    getIsResponseInserted(activecontactId)
  );
  const isResponseSent: boolean | undefined = useSelector(
    getIsResponseSent(activecontactId)
  );
  const editorRef = useRef<any>('');
  const isEnabled =
    activeInteraction?.hasAbilityToQuoteMessage &&
    (selectedCallContact?.contactStatus === VoiceContactStatus.ACTIVE ||
      selectedCallContact?.contactStatus === DigitalChannelStatus.OPEN);
  const theme = useTheme();
  const classes = ccfEditorStyles(theme);
  const isSend = useSelector(getIsSent);
  const isInsert = useSelector(getIsInsert);
  // const activeContactId = useSelector(getActiveInteractionID);
  const activeInteractionID: string = useSelector(getActiveInteractionID) || '';
  const copilotConfigurations = useSelector(getCopilotConfig);
  const index = copilotConfigurations?.path ?? 'custom';
  const isLiveCopilotEnabled = activecontact?.copilotSource === 'Live LLM';
  const namespace = uuidv5('namespace', namespaceNIL);
  const [shouldAddImage, setShouldAddImage] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const agentName = activecontact?.customerChatReplyJsonData !== undefined ? activecontact?.customerChatReplyJsonData[0]?.agentName?.split(' ')[0] : 'Agent';
  const [localConfiguration, setLocalConfiguration] = useState(copilotConfigurations);
  const locale = localConfiguration?.language;

  useEffect(() => {
    if (shouldAddImage && imageUrl) {
      insertImage(imageUrl);
      setShouldAddImage(false);
      editorRef.current.focusEditor();
    }
  }, [shouldAddImage, imageUrl]);


  // eslint-disable-next-line @nice-cxone/ccf/required-tsdoc
  const insertImage = (url: any) => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'IMAGE',
      'IMMUTABLE',
      { src: url, height: '72px', width: '72px', alignment: 'left'}
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = AtomicBlockUtils.insertAtomicBlock(
      editorState,
      entityKey,
      ' '
    );
    setEditorState(EditorState.forceSelection(newEditorState, newEditorState.getCurrentContent().getSelectionAfter()));
  };


  // eslint-disable-next-line @nice-cxone/ccf/required-tsdoc
  const uploadImageCallback = (file: any) => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    return new Promise((resolve, reject) => {
      resolve({ data: { link: url } });
    });
  };

  /**
   * Function on editor state change
   * @param editorState - any
   * @example - onEditorStateChange()
   */
  const onEditorStateChange = (editorState: any) => {
    const blocks = convertToRaw(editorState.getCurrentContent()).blocks;
    const value = blocks
      .map((block) => (!block.text.trim() && '\n') || block.text)
      .join('\n');
    setData(value);
    setEditorState(editorState);
  };

  const toolBarOptions = {
    options: ['inline', 'textAlign'],
    inline: {
      options: ['bold', 'italic', 'underline'],
      bold: { className: 'noBorder' },
      italic: { className: 'noBorder' },
      underline: { className: 'noBorder' },
    },
    textAlign: {
      options: ['left', 'center', 'right'],
      left: { className: 'noBorder' },
      center: { className: 'noBorder' },
      right: { className: 'noBorder' },
    },
  };

  const noToolBarOptions = {
    options: [],
    image: {
      uploadCallback: uploadImageCallback,
      previewImage: true,
      alt: { present: true, mandatory: false },
    },
  };

  /**
   *
   */
  const shiftEnterFunction = useCallback(
    (e: any) => {
      if (e.key === 'Enter' && e.shiftKey && data) {
        sendMessage(undefined, true);
      }
    },
    [data]
  );

  /**
   *@example function to insert dynamic text to editor
   */
   const insertText = (text: any, editorState: any) => {
    const currentContent = editorState.getCurrentContent(),
      currentSelection = editorState.getSelection();

    const newContent = Modifier.replaceText(
      currentContent,
      currentSelection,
      text
    );
    const newEditorState = EditorState.push(
      editorState,
      newContent,
      'insert-characters'
    );
    return EditorState.forceSelection(
      newEditorState,
      newContent.getSelectionAfter()
    );
  };

  useEffect(() => {
    if (
      selectedCallContact?.isElevated &&
      selectedCallContact?.copilotAssist === 'Yes' &&
      contactElevatedDetails?.has('sms')
    ) {
      setTimeout(() => {
        setEditorState(
          insertText(contactElevatedDetails.get('sms')?.message[0], editorState)
        );
        editorRef.current?.focusEditor();
        setData(contactElevatedDetails.get('sms')?.message[0]);
      }, 500);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', shiftEnterFunction, false);
    return () => {
      document.removeEventListener('keydown', shiftEnterFunction, false);
    };
  }, [shiftEnterFunction]);

  useEffect(() => {
    if (isSend.clicked) {
      sendMessage(isSend.message);
      // setTimeout(() => {
      dispatch(setIsSentClicked({ clicked: false, message: '' }));
      // }, 0);
    }
    if (isInsert.clicked) {
      insertNBRHandler(isInsert.message);
      // setTimeout(() => {
      dispatch(setIsInsertClicked({ clicked: false, message: '' }));
      // }, 0);
    }
  }, [isSend, isInsert]);

  useEffect(() => {
    if (isResponseSent) {
      sendMessage(sentBestResponse);
    }
  }, [isResponseSent]);

  useEffect(() => {
    if (isResponseInserted) {
      insertNBRHandler(sentBestResponse);
    }
  }, [isResponseInserted]);

  /**
   * Function to send message
   * @example - sendMessage()
   */
  const sendMessage = (NBRMessage = data, isManualSend = false) => {
    if (
      (data.trim() || NBRMessage.trim() || hasStagedAssets) &&
      imageUrl === ''
    ) {
      //dispatch(messageStateActions.messageQueue({value: data, id: count, isClientMessage: false, time: 'now', conferenceMsg: false}));
      const assetMarker =
        advanceCopilotFlow && !isManualSend
          ? parseAssetMarker(NBRMessage)
          : null;
      let structuredPayload;
      if (assetMarker) {
        structuredPayload = {
          text: '',
          linkContent: assetMarker.type === 'link' ? [assetMarker.value] : [],
          imageContent: assetMarker.type === 'image' ? [assetMarker.value] : [],
          processContent: [],
        };
      } else if (hasStagedAssets && isManualSend) {
        structuredPayload = {
          text: NBRMessage,
          linkContent: selectedCopilotAssets.webLinks,
          imageContent: selectedCopilotAssets.images,
          processContent: selectedCopilotAssets.processes,
        };
      }
      props.getTextCB(NBRMessage, structuredPayload);
      if (advanceCopilotFlow) {
        // knowledge-base sends/inserts must not advance the scripted flow
        if (isManualSend && !stopFlowJourneyRef.current) {
          dispatch(updateFlowMsgIndex(flowMsgIndex + 1));
        }
        stopFlowJourneyRef.current = false;
        if (hasStagedAssets) {
          dispatch(
            setSelectedCopilotAssets({ webLinks: [], images: [], processes: [] })
          );
        }
        dispatch(setIsAssetsAddedToCopilotSuggestion(false));
      }
      let interactionMSgCountCopy: any = [];

      const interactionIndex = interactionMSgCount.findIndex(
        (contact: any) => contact.interactionID === selectedCallContact?.id
      );
      const isInteractionPresent = interactionMSgCount.find(
        (contact: any) => contact.interactionID === selectedCallContact?.id
      );
      const writableisInteractionPresent = Object.assign(
        {},
        isInteractionPresent
      );
      if (interactionIndex >= 0 && isInteractionPresent) {
        writableisInteractionPresent.userChatMsgCount =
          isInteractionPresent.userChatMsgCount + 1;
        //interactionMSgCountCopy[interactionIndex] = writableisInteractionPresent;
        interactionMSgCountCopy = interactionMSgCount.map(
          (interaction: any) => {
            if (
              interaction.interactionID ===
              writableisInteractionPresent.interactionID
            ) {
              return {
                ...interaction,
                userChatMsgCount: isInteractionPresent.userChatMsgCount + 1,
              };
            }
            return interaction;
          }
        );
      } else {
        interactionMSgCountCopy = [
          ...interactionMSgCount,
          {
            interactionID: selectedCallContact ? selectedCallContact?.id : '',
            userChatMsgCount: 1,
          },
        ];
      }
      dispatch(setInteractionMsgCount(interactionMSgCountCopy));
      setData('');
      setEditorState(EditorState.createEmpty());
    }
    if (
      selectedCallContact?.isElevated &&
      selectedCallContact?.channel === DigitalChannelType.CHAT &&
      selectedCallContact?.copilotAssist === 'Yes' &&
      selectedCallContact?.copilotSource === 'JSON' &&
      contactElevatedDetails.has('sms')
    ) {
      const contactElevatedDetailsCopy = new Map();
      contactElevatedDetails.forEach(function (value: any, key: any) {
        if (key !== 'sms') {
          contactElevatedDetailsCopy.set(key, value);
        }
      });
      // const contactElevatedDetailsCopy = Object.assign(contactElevatedDetails);
      // contactElevatedDetailsCopy.delete('sms');
     
      dispatch(
        updateActiveContact({
          id: selectedCallContact?.id || '',
          updates: { elevationDetails: contactElevatedDetailsCopy },
        })
      );
    }
    if(imageUrl !== ''){
      const imagesResponse = [{
        type: 'image',
        isItemSent: true,
        isItemSelected: true,
        imgSrc: imageUrl,
        id: 1
      }];
      const replyMessage = {
        messageId: new Date().toString(),
        postId:
          'chat_1359751597379620865_b81029d6-f53b-406b-8bef-314d1eabb2341',
        isOwn: true,
        createdAt: formatMsgTime(),
        messageContent: {
          payload: {
            text: NBRMessage.trim(),
            linkContent: [],
            imageContent: imagesResponse ?? [],
            processContent: [],
          },
        },
        reactionStatistics: {
          likes: 0,
        },
        authorUser: {
          firstName: agentName,
          surname:'',
          emailAddress: 'customer.service@delta.com',
          loginUsername: '',
        },
        attachments: [],
        conferenceMsg: false,
        summaryCardName: '',
      };
      // dispatch(updateFileToBeUploaded(dataTransfer.files));
      dispatch(
        updateTwitterAgentReply({
          interactionId: activeInteractionID,
          replyMessage,
        })
      );
      setData('');
      setEditorState(EditorState.createEmpty());
    }
    editorRef.current.focusEditor();
    if (
      selectedCallContact?.channel === DigitalChannelType.CHAT &&
      selectedCallContact?.copilotAssist === 'Yes' &&
      selectedCallContact?.copilotSource === 'Live LLM' &&
      NBRMessage.trim()
    ) {
      const requestBody = {    
        "contactId": activecontact?.id,
        "busNo": 12345678,
        "tenantId": "12345678",
        "participantId": "agent",
        "messageBody": NBRMessage,
        "messageMetaData": {
            "completeUtterance": true
        },
        "transcriptionEventId": uuidv5(NBRMessage, namespace),
        "threshold_sales": copilotConfigurations?.threshold_sales ?? 0.75,
        "threshold_compliance": copilotConfigurations?.threshold_compliance ?? 0.75,
        "agentAssistAppConfig": {
            "language": locale?.toLowerCase(),
            "generativeResponseTone": copilotConfigurations?.generativeResponseTone ?? 'Formal',
            "channels": [
                "digital",
                "voice"
            ],
            "transcriptionProfileName": "google-test",
            "enableSentiment": copilotConfigurations?.enableSentiment ??  true,
            "sentimentResponseLength": copilotConfigurations?.sentimentResponseLength ?? 20,
            "realTimeSummary": copilotConfigurations?.realTimeSummary ?? true,
            "realTimeSummaryResponseLength": copilotConfigurations?.realTimeSummaryResponseLength ?? 25,
            "finalSummary": copilotConfigurations?.finalSummary ?? true,
            "finalSummaryResponseLength": copilotConfigurations?.finalSummaryResponseLength ?? 65,
            "behavioralGuidance": true,
            "behavioralGuidanceResponseLength": 10,
            "behevioralGuidanceNumberOfResponse": 3,
            "enableExpertCopilotIntegration": true,
            "expertCopilotURL": copilotConfigurations?.expertCopilotURL ?? '',
            "expertCopilotClientKey": copilotConfigurations?.expertCopilotClientKey ?? '',
            "expertCopilotClientSecret": copilotConfigurations?.expertCopilotClientSecret ?? '',
            "path": copilotConfigurations?.path ??'',
            "botPersona": copilotConfigurations?.botPersona ?? '',
            "kBAnswersPath": copilotConfigurations?.kBAnswersPath ??'',
            "salesOffersPath": copilotConfigurations?.salesOffersPath ??'',
            "compliancePath": copilotConfigurations?.compliancePath ??'',
            "knowledgeBaseArticles": copilotConfigurations?.knowledgeBaseArticles ?? true,
            "knowledgeBaseArticlesResponseLength": copilotConfigurations?.knowledgeBaseArticlesResponseLength ?? 65,
            "knowledgeBaseWebLinks": copilotConfigurations?.knowledgeBaseWebLinks ?? true,
            "knowledgeBaseMaximumWebLinks": copilotConfigurations?.knowledgeBaseMaximumWebLinks ?? 3,
            "knowledgeBaseImages": copilotConfigurations?.knowledgeBaseImages ?? true,
            "knowledgeBaseMaximumImages": copilotConfigurations?.knowledgeBaseMaximumImages ?? 3,
            "knowledgeBaseProcessSteps": copilotConfigurations?.knowledgeBaseProcessSteps ?? true,
            "knowledgeBaseMaximumSteps":copilotConfigurations?.knowledgeBaseMaximumSteps ?? 10,
            "threshold": copilotConfigurations?.threshold ?? 0.75,
            "vendorId": "agentCopilot",
            "agentAssistId": "Ved_acp_test123",
            "scriptParams": "",
            "backAndForthLiveConvo": copilotConfigurations?.backAndForthLiveConvo ?? false,
            "backAndForthLiveConvoLength": copilotConfigurations?.backAndForthLiveConvoLength ?? 4,
        },
        "messageIsCompletedUtterance": true,
        "agentId": 1898,
        "mediaType": activecontact?.media === 'Voice'? 'Voice': 'Digital',
        "totalAudioChunksInMilliSeconds": 2040
      };
      new Promise<void>((resolve, reject) => {
        callApi(
          API_BASE_URL + PROCESS_UTTERANCE,
          'POST',
          requestBody,
          dispatch
        )
          .then((response: any) => {
            // processApiResponse(response.data, dispatch);
            resolve();
          })
          .catch((error: any) => {
            console.error(
              'There was a problem with the fetch operation from editor:',
              error
            );
            reject(error);
          });
      });
    }
    document.getElementById('bodyContainer')?.scroll({
      top: document.getElementById('bodyContainer')?.scrollHeight, //scroll to the bottom of the element
      behavior: 'smooth', //auto, smooth, initial, inherit
    });
    setImageUrl('');
  };

  /**
   * method to handle insert response
   * @param response - response from the insert action
   * @example
   * ```
   * insertNBRHandler('response');
   * ```
   */
  const insertNBRHandler = (response: string| any) => {
    if (advanceCopilotFlow) {
      stopFlowJourneyRef.current = true;
      const assetMarker = parseAssetMarker(response);
      if (assetMarker) {
        dispatch(setIsAssetsAddedToCopilotSuggestion(true));
        dispatch(
          setSelectedCopilotAssets({
            ...selectedCopilotAssets,
            webLinks:
              assetMarker.type === 'link'
                ? [...selectedCopilotAssets.webLinks, assetMarker.value]
                : selectedCopilotAssets.webLinks,
            images:
              assetMarker.type === 'image'
                ? [...selectedCopilotAssets.images, assetMarker.value]
                : selectedCopilotAssets.images,
          })
        );
        return;
      }
    }
    if (typeof response !== 'string'){
      console.log('Response URL:', response.url);
      setShouldAddImage(true);
      setImageUrl(response.url);
    }
    else{
    const contentState = ContentState.createFromText(response);
    const newEditorState = EditorState.createWithContent(contentState);
    setEditorState(newEditorState);
    setData(response);
    editorRef.current.focusEditor();
    }
  };

  /**
   * funtion to cancel data
   * @example cancelData()
   */
  const cancelData = () => {
    setData('');
    setEditorState(EditorState.createEmpty());
    if (hasStagedAssets) {
      dispatch(
        setSelectedCopilotAssets({ webLinks: [], images: [], processes: [] })
      );
      dispatch(setIsAssetsAddedToCopilotSuggestion(false));
    }
  };

  /**
   * Function to render custom button
   * @example renderCustomButton()
   * @returns
   */
  const renderCustomButton = () => {
    return (
      <div className={'sendButtonContainer'}>
        {isLiveCopilotEnabled && (
          <div className={'custumIconWrapper'}>
            <CcfUploadIcon className="attachment" />
            {false && <CcfImageIcon className="alignStyles" />}
            <CcfEmojiIcon style={{ marginTop: '3px' }} />
            {false && <CcfGifIcon className="alignStyles" />}
            <FormatTextdirectionLToR className="alignStyles" />
            <FormatTextdirectionRToL className="alignStyles" />
          </div>
        )}
        <div
          style={{
            width: '100%',
            display: 'flex',
            marginTop: '3px',
            marginLeft: '108px',
            justifyContent: 'flex-end',
          }}
        >
          <div className={'emptyspaces'}></div>
          <div className={'custumButtonWrapper'}>
            {!isLiveCopilotEnabled && (
              <CcfButton
                disabled={!isEnabled}
                className={'quickRepliesButton'}
                onClick={() =>
                  dispatch(updateAppSpaceTab({ tab: AppSpace.QUICK_REPLY }))
                }
                id="agent_webChat_repliesButton"
              >
                <CcfFlashIcon />
                <div className="quickRepliesText">{translate('replies')}</div>
              </CcfButton>
            )}
            {isLiveCopilotEnabled && isEnabled && data.trim() && (
              <Button
                onClick={() => cancelData()}
                size="small"
                id="agent_webChat_cancelButton"
              >
                <CancelOutlined />
              </Button>
            )}
            {!isLiveCopilotEnabled && (
              <CcfButton
                onClick={() => cancelData()}
                className={'quickRepliesButton'}
                id="agent_webChat_cancelButton"
              >
                {translate('cancel')}
              </CcfButton>
            )}
            <CcfButton
              disabled={
                !(
                  (isEnabled && (data.trim() || hasStagedAssets)) ||
                  imageUrl !== ''
                )
              }
              style={{ padding: '7px' }}
              className={
                isEnabled && (data?.trim() || hasStagedAssets)
                  ? 'sendButton'
                  : 'disableButton'
              }
              onClick={() => sendMessage(undefined, true)}
              id="agent_webChat_sendReplyButton"
            >
              {isLiveCopilotEnabled ? translate('sendMessage') : <Send />}
            </CcfButton>
          </div>
        </div>
      </div>
    );
  };
  /**
   * Function to render upload icon
   * @example renderUploadIcon()
   * @returns
   */
  const renderUploadIcon = () =>
    activeInteraction?.hasAbilityToSendFiles ? (
      <div>
        <CcfUploadIcon className="attachment" />
      </div>
    ) : (
      <div />
    );

  /**
   * Function to set height of interaction post
   * @param event -any
   * @example isEmail
   */
  const formatMsgTime = () => {
    const date = new Date();
    const result = date.toUTCString();
    return result;
  };

  return (
    <>
      <Editor
        ref={editorRef}
        toolbar={isLiveCopilotEnabled ? noToolBarOptions : toolBarOptions}
        toolbarHidden={false}
        editorState={editorState}
        toolbarClassName={'toolbarClassName'}
        wrapperClassName={styles['wrapperClassName']}
        editorClassName={'editorClassName'}
        onEditorStateChange={onEditorStateChange}
        toolbarCustomButtons={[renderCustomButton()]}
        spellCheck={true}
      />
      {hasStagedAssets && (
        <CcfBox style={{ padding: '4px 8px' }}>
          {selectedCopilotAssets.webLinks.map((link: any, i: number) => (
            <div key={`link-${i}`}>
              Link: <span style={{ color: '#0071bb' }}>{link}</span>
            </div>
          ))}
          {selectedCopilotAssets.images.map((image: any, i: number) => (
            <img
              key={`image-${i}`}
              alt={`${i}`}
              src={image}
              style={{ height: '72px', width: '72px', objectFit: 'cover' }}
            />
          ))}
        </CcfBox>
      )}
    </>
  );
}

export default CcfTextEditor;
